import { useCallback } from "react"
import { Box } from "@mui/system"
import { ClickAwayListener, Typography } from "@mui/material"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import { UseRfidContext } from "layouts/rfid/context/provider.tsx"
import {  RfidCellComponent, RfidInfo } from "types/rfid"
import { SmallButton } from "components/button/SmallButton.tsx"
import ReplayIcon from "@mui/icons-material/Replay"
import DoNotDisturbAltOutlinedIcon from "@mui/icons-material/DoNotDisturbAltOutlined"
import RuleIcon from "@mui/icons-material/Rule"
import { useChargingRulesContext } from "layouts/charging-rules/context/useChargingRulesContext.ts"
import { RfidStatus } from "layouts/rfid/utils/const.ts"

export const RfidNumberCell: RfidCellComponent = ({ rfid } : { rfid: RfidInfo }) => {
    const {  setCurrentCardSelected, currentCardSelected, tableButton, setTableButton } = UseRfidContext()
    const isSelected = currentCardSelected && currentCardSelected?.id === rfid.id && !tableButton

    const onSelect = () => {
        if (isSelected) {
            setCurrentCardSelected(null)
        } else {
            setCurrentCardSelected(rfid)
            setTableButton(false)
        }
    }

    return (
        <Box
            sx={{
                position: "relative",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "1.5em",
                opacity: 1,
                width: "100%",

            }}
        >
            <Typography sx={{ opacity: rfid.status === RfidStatus.Deactivate ? 0.5 : 1 }}>
                {rfid.rfidNumber.toUpperCase()}
            </Typography>
            <Box
                sx={{
                    position: "relative",
                    marginLeft: "auto",
                    display: 'inline-flex',
                    '&:hover': {
                        zIndex: 1500,
                    }
                }}
            >
                <MoreVertIcon
                    sx={{
                        p: 0,
                        m: 0,
                        height: "0.5rem",
                        width: "0.5rem",
                        transform: "scale(3.5)",
                        color: isSelected ? "secondary.main" : "grey.600",
                        transition:
                            "color 0.2s ease-in-out, opacity 0.2s ease-in-out",
                        "&:hover": {
                            color: "secondary.main",
                            opacity: isSelected ? 0.5 : 1,
                        },
                        cursor: "pointer",
                    }}
                    onClick={onSelect}
                />
            </Box>
            {isSelected && <RfidMenu rfid={rfid} />}
        </Box>
    )
}

const RfidMenu = ({rfid} : {rfid: RfidInfo}) => {
    const {
        closeRfidPopup,
        setCurrentCardSelected,
        currentCardSelected,
        stateDisableCard,
        stateReactivateCard,
        setViewMode,
        rfidCurrentStatus
    } = UseRfidContext()
    const { setRfidCardRuleId } = useChargingRulesContext()

    const onClick = () => {
        setRfidCardRuleId(rfid.id)
        setCurrentCardSelected(rfid)
        setViewMode("EDIT")
    }

    const handleClickAway = useCallback(() => {

        if (rfidCurrentStatus && rfidCurrentStatus() !== 0) return;

        closeRfidPopup();
        if (currentCardSelected) {
            setCurrentCardSelected(null);
        }
    }, [closeRfidPopup, currentCardSelected, setCurrentCardSelected, rfidCurrentStatus]);


    return (
        <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown" touchEvent="onTouchStart">
            <Box
                sx={{
                    position: "absolute",
                    right: "0",
                    zIndex: 1500,
                    transform: "translate(105%, 0%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: "0.4rem",
                    opacity: 1,
                }}
                onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        handleClickAway();
                    }
                }}
            >
                {rfid.status === RfidStatus.Active ? (
                    <SmallButton
                        sx={{
                            borderRadius: 2.5,
                            '&:hover': {
                                bgcolor: (theme) => `${theme.palette.grey[200]} !important`,
                            },
                        }}
                        startIcon={<RuleIcon color="primary" />}
                        onClick={onClick}
                    >
                        <Typography
                            variant="titleSmall"
                            sx={{
                                color: "primary.main",
                                py:"0.3rem",
                            }}
                        >
                            Modifier les règles
                        </Typography>
                    </SmallButton>
                ): null}

                {rfid.status === RfidStatus.Active ? (
                    <SmallButton
                        sx={{
                            borderRadius: 2.5,
                            bgcolor: () => "status.warning !important",
                            '&:hover': {
                                bgcolor: () => `#FFD4A5 !important`,
                            },
                        }}
                        startIcon={
                            <DoNotDisturbAltOutlinedIcon sx={{ color: "waiting.main" }} />
                        }
                        onClick={() => {
                            stateDisableCard()
                        }}
                    >
                        <Typography
                            variant="titleSmall"
                            sx={{
                                color: "waiting.main",
                                py:"0.3rem",
                            }}>Désactiver la carte</Typography>
                    </SmallButton>
                ) : (
                    <SmallButton
                        sx={{
                            borderRadius: 2.5,
                            bgcolor: (theme) => `${theme.palette.status.success} !important`,
                            '&:hover': {
                                bgcolor: '#D1F5C0 !important',
                            },
                            transition: 'all 0.2s ease-in-out',
                        }}
                        onClick={stateReactivateCard}
                        startIcon={
                            <ReplayIcon sx={{
                                color: "success.main",
                                transition: 'color 0.2s ease-in-out',
                            }}
                            />
                        }
                    >
                        <Typography
                            variant="titleSmall"
                            sx={{
                                color: "success.main",
                                py:"0.3rem",
                            }}
                        >
                            Réactiver la carte
                        </Typography>
                    </SmallButton>
                )}
            </Box>
        </ClickAwayListener>
    )

}
